/**
 * payments.ts
 * ──────────────────────────────────────────────────────────────
 * Client side of the KulPay checkout: the API shapes, and the small amount of
 * local state needed to survive the round trip to the gateway.
 *
 * WHY LOCAL STATE AT ALL. Paying leaves the app entirely. On a phone the
 * student is then pushed an M-Pesa prompt, confirms it on the handset, and
 * comes back to Tutor whenever they feel like it, often by reopening the app
 * rather than by any redirect. So the reference cannot live in a React state
 * variable or a URL parameter: both are gone by the time they return. It is
 * written to localStorage before we navigate away and read back on the way in.
 *
 * This also means the flow does not depend on the gateway redirecting the
 * browser anywhere. If it does, we pick the payment up on arrival; if it never
 * does, we pick it up the next time the student opens the pricing screen.
 *
 * Nothing here decides whether the student paid. Only the server does, via
 * GET /subscription/payments/:reference, which reflects a signature-verified
 * webhook. A student who lands back on the app having done nothing sees the
 * pending state and then a failure, never access.
 */

import { api } from './api'

/**
 * Mirror of the backend ENABLE_PAID_TIERS flag. Every paid surface respects
 * it, so it lives here rather than being redeclared per page: copies drift,
 * and the failure mode is a student being offered something they cannot buy.
 *
 * Flip with VITE_ENABLE_PAID_TIERS=true on the web app, paired with
 * ENABLE_PAID_TIERS=true on the API.
 */
export const PAID_TIERS_ENABLED = import.meta.env.VITE_ENABLE_PAID_TIERS === 'true'

/**
 * Send the student to the pricing page in a NEW browsing context.
 *
 * Every "Ver planos" in the app goes through here so the behaviour is
 * uniform. Two reasons it is not a normal in-app navigation:
 *
 *   - Inside an installed PWA this hands off to the real browser. The
 *     standalone shell has no URL bar and no back button, which is a bad
 *     place to run a payment the student cannot inspect or escape.
 *   - The app keeps its place. A student who hits the follow-up cap mid-chat
 *     comes back to that chat still open, rather than to wherever a
 *     navigation dumped them. That is why this exists at all.
 *
 * Must be called directly from a click handler. Deferred behind an await it
 * stops counting as a user gesture and gets blocked, silently on iOS.
 */
export function openPricing(path = '/pricing'): void {
  const opened = window.open(path, '_blank')
  if (opened) {
    // Same-origin, so this is allowed and severs the reverse reference.
    try { opened.opener = null } catch { /* already detached */ }
    return
  }
  // Popup blocked. Losing the student entirely is worse than navigating away.
  window.location.href = path
}

// ── API shapes ──────────────────────────────────────────────────────────

export interface OfferTerm {
  months:          number
  /** Tier price before any discount. Rendered struck through when it differs. */
  list_amount_mzn: number
  /** What the student actually pays. */
  amount_mzn:      number
}

export interface Offer {
  tier_code:   string
  tier_name:   string
  description: string | null
  percent_off: number
  terms:       OfferTerm[]
}

export interface CurrentSubscription {
  tier_id:    string | null
  status:     string
  expires_at: string | null
}

export interface OfferResponse {
  offers:  Offer[]
  current: CurrentSubscription | null
}

/** A full comp returns `granted`; everything else returns a checkout URL. */
export interface CheckoutResponse {
  granted:       boolean
  expires_at?:   string
  checkout_url?: string
  reference?:    string
  amount_mzn?:   number
  expires_in_ms?: number
}

export type PaymentStatus =
  | 'created' | 'processing' | 'requires_action'
  | 'succeeded' | 'failed' | 'cancelled' | 'expired'

export interface PaymentView {
  status:         PaymentStatus
  amount_mzn:     number
  months:         number
  failure_reason: string | null
  tier_name:      string
  expires_at:     string | null
}

/** Mirrors the backend's TERMINAL list. A terminal payment stops the poll. */
const TERMINAL: PaymentStatus[] = ['succeeded', 'failed', 'cancelled', 'expired']

export function isTerminal(status: PaymentStatus): boolean {
  return TERMINAL.includes(status)
}

export function getOffer(): Promise<OfferResponse> {
  return api.get<OfferResponse>('/subscription/offer')
}

export function startCheckout(tierCode: string, months: number): Promise<CheckoutResponse> {
  return api.post<CheckoutResponse>('/subscription/checkout', {
    tier_code: tierCode,
    months,
  })
}

export function getPayment(reference: string): Promise<{ payment: PaymentView }> {
  return api.get<{ payment: PaymentView }>(
    `/subscription/payments/${encodeURIComponent(reference)}`,
  )
}

/**
 * Cheapest monthly price on offer, for an "a partir de" hook on paywall
 * surfaces.
 *
 * Deliberately returns a number and not a tier. Which tier unlocks which
 * feature is per-feature admin config, so a paywall that names one is a
 * guess that goes stale silently: before this, every wall in the app sold
 * "Plano Essencial", a tier that has not existed for some time. Naming a
 * price is safe because it comes from the same endpoint the pricing page
 * uses; naming a tier is not, so no paywall does it any more.
 *
 * Null when nothing is purchasable, which is also the pre-launch state.
 */
export function cheapestMonthlyMzn(offer: OfferResponse | null): number | null {
  if (!offer) return null
  const monthly = offer.offers
    .flatMap(o => o.terms.filter(t => t.months === 1))
    .map(t => t.amount_mzn)
  return monthly.length ? Math.min(...monthly) : null
}

// ── Pending payment ─────────────────────────────────────────────────────

const PENDING_KEY = 'tutor.pendingPayment'

/**
 * How long a reference stays worth polling. The checkout link itself dies
 * after 5 minutes, but the student may confirm on the handset right at the
 * edge of that and the webhook can lag, so this is generous. Past it the
 * entry is dropped rather than polled forever: the backend reconciler settles
 * the payment either way, and a stale card on the pricing screen is worse
 * than no card.
 */
const PENDING_TTL_MS = 2 * 60 * 60 * 1000

export interface PendingPayment {
  reference: string
  tierName:  string
  months:    number
  amountMzn: number
  startedAt: number
}

export function rememberPendingPayment(p: PendingPayment): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(p))
  } catch {
    // Private browsing or a full quota. The payment still completes; the
    // student just will not see the confirmation card on return, and the
    // subscription shows up on the profile once the webhook lands.
  }
}

export function readPendingPayment(): PendingPayment | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingPayment
    if (!parsed?.reference) return null
    if (Date.now() - (parsed.startedAt ?? 0) > PENDING_TTL_MS) {
      clearPendingPayment()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearPendingPayment(): void {
  try { localStorage.removeItem(PENDING_KEY) } catch { /* nothing to do */ }
}

// ── Formatting ──────────────────────────────────────────────────────────

/** `149` → `149 MZN`. Whole MZN throughout; the gateway never sees this. */
export function formatMzn(amount: number): string {
  return `${amount.toLocaleString('pt-MZ')} MZN`
}

export function formatExpiry(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('pt-MZ', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function termLabel(months: number): string {
  return months === 1 ? '1 mês' : `${months} meses`
}
